import {SSO_DOMAIN, HAO_DOMAIN} from './utils.js';

const SESSION_AUTHORITY_URL = `${SSO_DOMAIN}/v1/me`;

let onUnauthorized = () => {};
let installed = false;
let reprobeInFlight = null;

export function setOnUnauthorized(fn) {
  onUnauthorized = fn;
}

function requestUrl(input) {
  return typeof input === 'string' ? input : (input?.url || '');
}

function requestMethod(input, init) {
  return (init?.method || input?.method || 'GET').toUpperCase();
}

function isOwnBackend(url) {
  return url.startsWith(SSO_DOMAIN) || url.startsWith(HAO_DOMAIN);
}

function isSessionAuthorityRequest(url, method) {
  return url.startsWith(SESSION_AUTHORITY_URL) && method === 'GET';
}

// Single-flight re-probe of the session authority endpoint, using the
// native (unwrapped) fetch so this doesn't re-enter the interceptor.
function reprobeSession(nativeFetch) {
  if (!reprobeInFlight) {
    reprobeInFlight = nativeFetch(SESSION_AUTHORITY_URL, {method: 'GET', credentials: 'include'})
      .then(res => res.status === 200)
      .catch(() => false)
      .finally(() => { reprobeInFlight = null; });
  }
  return reprobeInFlight;
}

// Monkey-patches the global fetch once, at app start — the one true I/O
// choke point in this codebase (no shared axios-style client instance
// exists here), mirroring assesspro's axios response interceptor: a 401 on
// the session-authority call (GET /v1/me) means the session is dead; a 401
// elsewhere could just mean a stale/inaccessible resource reference, so it
// re-probes /v1/me once (single-flight) and only tears down if that also
// fails. A 403 anywhere is a real per-resource authorization denial for a
// still-valid session and is never treated as a teardown signal.
export function installAuthInterceptor() {
  if (installed) return;
  installed = true;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);
    const url = requestUrl(input);

    if (response.status === 401 && isOwnBackend(url)) {
      const method = requestMethod(input, init);
      if (isSessionAuthorityRequest(url, method)) {
        onUnauthorized();
      } else {
        reprobeSession(nativeFetch).then(stillValid => {
          if (!stillValid) onUnauthorized();
        });
      }
    }
    // 403s are intentionally ignored here: valid session, resource-specific
    // denial, left to whatever per-call error handling the caller already has.

    return response;
  };
}
